import { apiJson, apiStreamOptionalAuth } from '../api';

/** Another sentence from a shown sentence's article (the article is the shown one's). */
export interface ArticleSentence {
  id: string;
  sentence: string;
  /** For You only: its near-duplicate group, hidden along with the row it is folded into. */
  sentence_ids?: string[];
}

/** One interesting sentence on the public page (sentence.tldr.fm), and its article. */
export interface PublicSentence {
  id: string;
  sentence: string;
  /** The decision model's interest score, 0.6-1. */
  score: number;
  confidence: number;
  url: string;
  canonical_domain: string | null;
  title: string | null;
  published_at: string | null;
  scored_at: string;
  /** The article's other sentences, best first. The top list and For You give
   * each article one slot and fold the rest in here; search results and feeds
   * show every sentence, so it is empty there. */
  more_from_article: ArticleSentence[];
}

/** A search result: the sentence plus the decision model's relevance verdict for the query. */
export interface SearchSentence extends PublicSentence {
  relevance: number;
  relevance_confidence: number;
  /** Near-duplicates collapsed into this one. */
  copies: number;
  /** Every displayed group member, for atomic hide and undo. */
  sentence_ids: string[];
}

export interface SentenceSearchRecord {
  id: string;
  query: string;
}

export interface PublicSentencesResponse {
  sentences: PublicSentence[];
}

/** Resolve or create a shareable sentence search row by query. */
export async function createSentenceSearch(query: string): Promise<SentenceSearchRecord> {
  return await apiJson<SentenceSearchRecord>('/api/sentence-searches', {
    method: 'POST',
    public: true,
    json: { query },
  });
}

/** Look up a sentence search query by its shareable UUID. */
export async function getSentenceSearch(searchId: string): Promise<SentenceSearchRecord> {
  return await apiJson<SentenceSearchRecord>(`/api/sentence-searches/${encodeURIComponent(searchId)}`, {
    public: true,
  });
}

/** The top public sentences in a time window ("24h", "72h", "7d"), by score
 * then confidence. No login. Searches go through ``streamSentenceSearch``. */
export async function listSentences(window: string): Promise<PublicSentencesResponse> {
  return await apiJson<PublicSentencesResponse>(`/api/sentences?${new URLSearchParams({ window })}`, { public: true });
}

/** One line of the search stream (``application/x-ndjson``). */
export type SentenceSearchEvent =
  | { type: 'search'; search_id: string; query: string }
  | { type: 'results'; sentences: SearchSentence[]; pending: number; judged: number; done: boolean }
  | { type: 'error'; detail: string };

/** Where a streaming search stands: each results line replaces the list whole. */
export interface SentenceSearchState {
  searchId: string | null;
  query: string;
  sentences: SearchSentence[];
  /** Whether any results line has arrived (the first comes from cache). */
  hasResults: boolean;
  /** Candidates still being judged. */
  pending: number;
  judged: number;
  done: boolean;
  error: string;
}

export const INITIAL_SEARCH_STATE: SentenceSearchState = {
  searchId: null, query: '', sentences: [], hasResults: false, pending: 0, judged: 0, done: false, error: '',
};

/** Split newline-delimited text: complete lines out, the unterminated tail kept for the next chunk. */
export function splitLines(buffered: string, chunk: string): { lines: string[]; rest: string } {
  const parts = (buffered + chunk).split('\n');
  const rest = parts.pop() ?? '';
  return { lines: parts.map((line) => line.trim()).filter(Boolean), rest };
}

/** Fold one stream event into the search state. An error ends the stream and keeps what was shown. */
export function applySearchEvent(state: SentenceSearchState, event: SentenceSearchEvent): SentenceSearchState {
  switch (event.type) {
    case 'search':
      return { ...state, searchId: event.search_id, query: event.query };
    case 'results':
      return { ...state, sentences: event.sentences, hasResults: true, pending: event.pending, judged: event.judged, done: event.done };
    case 'error':
      return { ...state, error: event.detail || 'Search failed', done: true };
  }
}

/** Search the store by meaning, judged for relevance by the decision model,
 * streaming: ``onUpdate`` gets the state after every line (cached results
 * first, then more as judging finishes). Signed in, the reader's hides apply.
 * Resolves with the final state; rejects if aborted or the request fails. */
export async function streamSentenceSearch(
  { q, searchId, window, signal, onUpdate }: {
    q?: string;
    searchId?: string;
    window: string;
    signal?: AbortSignal;
    onUpdate: (state: SentenceSearchState) => void;
  },
): Promise<SentenceSearchState> {
  const params = new URLSearchParams();
  if (searchId) params.set('search_id', searchId);
  else if (q) params.set('q', q);
  params.set('window', window);
  const response = await apiStreamOptionalAuth(`/api/sentences/search?${params}`, signal);
  if (!response.body) throw new Error('Search returned no stream');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let state = INITIAL_SEARCH_STATE;
  let rest = '';
  const consume = (lines: string[]) => {
    for (const line of lines) {
      state = applySearchEvent(state, JSON.parse(line) as SentenceSearchEvent);
      onUpdate(state);
    }
  };
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    const split = splitLines(rest, decoder.decode(value, { stream: true }));
    rest = split.rest;
    consume(split.lines);
  }
  consume(splitLines(rest, decoder.decode() + '\n').lines);
  if (!state.done) {
    state = { ...state, done: true, error: 'The search ended early; showing what was judged.' };
    onUpdate(state);
  }
  return state;
}
